import axios from 'axios';
import router from '@/router';
import { ElMessage } from 'element-plus';


// 1. 从环境变量优先获取 baseURL，兼容硬编码 fallback
const baseURL = process.env.VUE_APP_API_BASE_URL || 'https://xlfk.duvvv.cn/api1/';

// 2. 不需要携带 token 的接口路径（Set 优化查找）
const NO_TOKEN_PATHS = new Set([
    '/api/physicians/checkLogin',
    '/api/questionnaires/addOrUpdate'
]);

// 3. 创建 axios 实例
const service = axios.create({
    baseURL,
    timeout: 30000, // 延长超时时间（导出文件可能耗时较长）
    withCredentials: false
});

// 4. 请求拦截器
service.interceptors.request.use(
    (config) => {
        // 忽略不需要 token 的接口
        if (!NO_TOKEN_PATHS.has(config.url)) {
            const token = localStorage.getItem('userToken');
            if (token) {
                config.headers['Authorization'] = `Bearer ${token}`;
            }
        }
        return config;
    },
    (error) => {
        console.error('【请求拦截器错误】:', error);
        ElMessage.error('请求发送失败，请检查网络');
        return Promise.reject(error);
    }
);

// 5. 响应拦截器（关键修复：根据 responseType 决定返回内容）
service.interceptors.response.use(
    (response) => {
        // 情况1：responseType 为 blob（文件流），返回完整响应对象（需要 headers 和 data）
        if (response.config.responseType === 'blob') {
            return response; // 不剥离 headers，返回完整响应
        }

        // 情况2：普通接口（JSON 响应），返回 response.data
        const res = response.data;
        // 假设后端统一返回格式：{ success: boolean, message: string, data: any }
        if (res?.success === false) {
            ElMessage.error(res.message || '操作失败');
            return Promise.reject(res);
        }
        return res;
    },
    (error) => {
        console.error('【响应拦截器错误】:', error);
        let errorMessage = '请求失败，请稍后重试';

        if (error.response) {
            const status = error.response.status;
            const resData = error.response.data;

            switch (status) {
                case 401:
                    errorMessage = '登录已过期，请重新登录';
                    localStorage.removeItem('userToken');
                    // 避免重复跳转登录页
                    if (router.currentRoute.path !== '/denglu' && router.currentRoute.path !== '/') {
                        router.push({
                            path: '/',
                            query: { redirect: router.currentRoute.fullPath }
                        });
                    }
                    break;
                case 403:
                    errorMessage = '您没有权限执行此操作';
                    break;
                case 404:
                    errorMessage = '请求的接口不存在';
                    break;
                case 500:
                    errorMessage = '服务器内部错误，请联系管理员';
                    break;
                default:
                    // 尝试解析 blob 类型的错误信息（导出失败时可能返回）
                    if (resData instanceof Blob) {
                        return new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onload = (e) => {
                                try {
                                    const errObj = JSON.parse(e.target.result);
                                    errorMessage = errObj.message || '操作失败';
                                } catch (err) {
                                    errorMessage = '服务器返回未知错误';
                                }
                                ElMessage.error(errorMessage);
                                reject(new Error(errorMessage));
                            };
                            reader.readAsText(resData);
                        });
                    } else if (resData?.message) {
                        errorMessage = resData.message;
                    }
            }
        } else if (error.request) {
            // 无响应（网络错误）
            errorMessage = '网络异常，请检查连接';
            // 注释掉强制跳转登录页（网络错误不一定是登录问题）
            // router.push('/denglu');
        } else {
            errorMessage = `请求设置错误: ${error.message}`;
        }

        ElMessage.error(errorMessage);
        return Promise.reject(new Error(errorMessage));
    }
);

// export default service;

// 导出一个函数，而不是实例本身
export default function request(config) {
    // 判断请求方法是否存在，如果不存在，默认使用 'get'
    const method = config.method?.toLowerCase() || 'get';

    // 根据请求方法，调用 service 实例对应的方法
    if (method === 'get' || method === 'delete') {
        return service[method](config.url, { params: config.params, ...config });
    } else { // post, put, patch 等
        return service[method](config.url, config.data, config);
    }
}